/**
 * OmegaLaunchPlansPage — Phase 26 "morning coffee review" UX.
 *
 * Card-grid view of all ProductLaunchPlans with filtering, sorting,
 * bulk actions, and a right-side detail drawer with founder-signed
 * approve / defer / reject flows.
 */

import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLaunchPlans, usePlanMemoryContext, omegaQueryKeys } from '../hooks/useOmegaState';
import type { LaunchPlanSummary, MemoryEntry } from '../data/types';
import { signHashWithFounderKey } from '@/api/real/founderSign';
import { getAccessToken } from '@/api/supabase';
import { apiBaseUrl } from '@/api/http';
import '../theme/omega-theme.css';

// ─── Constants ─────────────────────────────────────────────────────

const DOMAIN_LIST = [
  'healthcare', 'logistics', 'finance', 'education', 'real_estate',
  'creative_tools', 'government', 'games', 'productivity', 'developer_tools',
  'consumer_apps', 'ecommerce', 'ai_tooling', 'streaming_media', 'music_audio',
  'video_animation', 'marketing_ads', 'hr_recruiting', 'legal_tech',
  'travel_hospitality', 'food_restaurant', 'fitness_wellness', 'crypto_web3',
  'other',
];

const STATUS_OPTIONS = [
  'ready_for_review',
  'validated',
  'approved',
  'rejected',
  'deferred',
  'draft',
  'enriching',
  'stalled_blocked',
];
const REVIEW_STATUSES = new Set(['ready_for_review', 'validated', 'approved']);
const QUEUE_STATUSES = new Set(['draft', 'enriching', 'stalled_blocked']);

const PRICING_OPTIONS = [
  'freemium', 'subscription', 'per_seat', 'usage_based', 'one_time',
  'marketplace', 'enterprise', 'free', 'open_source',
];

type SortField = 'confidence' | 'year1_revenue' | 'market_size' | 'build_time' | 'founder_score';

// ─── Helpers ───────────────────────────────────────────────────────

function fmt(n: number): string {
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n}`;
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function founderScore(plan: LaunchPlanSummary): number {
  return (
    plan.confidence * 0.5 +
    Math.min(plan.growth_rate_yoy, 1.0) * 0.3 +
    Math.min(Math.log10(plan.market_size_usd + 1) / 12, 1) * 0.2
  );
}

async function sha256hex(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

function sortKeys(obj: unknown): unknown {
  if (Array.isArray(obj)) return obj.map(sortKeys);
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, v]) => [k, sortKeys(v)]),
    );
  }
  return obj;
}

async function callAdminAction(
  planId: string,
  action: string,
  body: Record<string, unknown>,
): Promise<void> {
  const canonical = JSON.stringify(sortKeys(body));
  const hashHex = await sha256hex(canonical);
  const sig = await signHashWithFounderKey(hashHex);
  const token = await getAccessToken();
  if (!token) throw new Error('Not authenticated');
  const resp = await fetch(`${apiBaseUrl()}/admin/launchplans/${planId}/${action}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'X-Founder-Signature': sig,
    },
    body: canonical,
  });
  if (!resp.ok) {
    const err = (await resp.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `HTTP ${resp.status}`);
  }
}

async function callEnrichAction(planId: string): Promise<LaunchPlanSummary> {
  const body = { nonce: crypto.randomUUID() };
  const canonical = JSON.stringify(sortKeys(body));
  const hashHex = await sha256hex(canonical);
  const sig = await signHashWithFounderKey(hashHex);
  const token = await getAccessToken();
  if (!token) throw new Error('Not authenticated');
  const resp = await fetch(`${apiBaseUrl()}/admin/launchplans/${planId}/enrich`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'X-Founder-Signature': sig,
    },
    body: canonical,
  });
  if (!resp.ok) {
    const errBody = (await resp.json().catch(() => ({}))) as { detail?: string };
    throw new Error(errBody.detail ?? `HTTP ${resp.status}`);
  }
  return resp.json() as Promise<LaunchPlanSummary>;
}

async function forceDiscovery(): Promise<void> {
  const body = { nonce: crypto.randomUUID() };
  const canonical = JSON.stringify(sortKeys(body));
  const hashHex = await sha256hex(canonical);
  const sig = await signHashWithFounderKey(hashHex);
  const token = await getAccessToken();
  if (!token) throw new Error('Not authenticated');
  const resp = await fetch(`${apiBaseUrl()}/admin/marketmap/update`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'X-Founder-Signature': sig,
    },
    body: canonical,
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
}

// ─── Sub-components ────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    ready_for_review: '#60a5fa',
    validated: 'var(--omega-green)',
    approved: '#6ee7b7',
    draft: 'var(--omega-ink-subtle)',
    enriching: '#a78bfa',
    stalled_blocked: 'var(--omega-red, #f87171)',
    rejected: 'var(--omega-red, #f87171)',
    deferred: '#fbbf24',
  };
  const label: Record<string, string> = {
    ready_for_review: 'ready',
    stalled_blocked: 'stalled',
  };
  return (
    <span
      style={{
        color: colors[status] ?? 'var(--omega-ink-subtle)',
        textTransform: 'uppercase',
        fontSize: '8pt',
        letterSpacing: '0.07em',
        fontWeight: 700,
        background: 'rgba(255,255,255,0.06)',
        padding: '1px 5px',
        borderRadius: 3,
      }}
    >
      {label[status] ?? status}
    </span>
  );
}

function ConfidenceRing({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const color = pct >= 75 ? 'var(--omega-green)' : pct >= 50 ? '#fbbf24' : '#f87171';
  const r = 10;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <svg width={26} height={26} viewBox="0 0 26 26">
        <circle cx={13} cy={13} r={r} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={3} />
        <circle
          cx={13}
          cy={13}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={3}
          strokeDasharray={`${dash} ${circ - dash}`}
          strokeLinecap="round"
          transform="rotate(-90 13 13)"
        />
      </svg>
      <span style={{ color, fontWeight: 700, fontSize: '9pt' }}>{pct}%</span>
    </span>
  );
}

function FounderScoreBar({ score }: { score: number }) {
  const pct = Math.round(score * 100);
  const color = pct >= 70 ? 'var(--omega-green)' : pct >= 45 ? '#fbbf24' : '#f87171';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div
        style={{
          flex: 1,
          height: 4,
          background: 'rgba(255,255,255,0.1)',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 2 }} />
      </div>
      <span style={{ color: 'var(--omega-ink-subtle)', fontSize: '8pt', minWidth: 28 }}>
        {pct}
      </span>
    </div>
  );
}

function Chip({
  children,
  color,
}: {
  children: React.ReactNode;
  color?: string;
}) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 6px',
        borderRadius: 3,
        fontSize: '8pt',
        background: color ? `${color}22` : 'rgba(255,255,255,0.07)',
        color: color ?? 'var(--omega-ink-subtle)',
        border: `1px solid ${color ? `${color}55` : 'var(--omega-border, rgba(255,255,255,0.1))'}`,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

// ─── Plan Card ─────────────────────────────────────────────────────

function PlanCard({
  plan,
  selected,
  onSelect,
  onClick,
}: {
  plan: LaunchPlanSummary;
  selected: boolean;
  onSelect: (id: string, checked: boolean) => void;
  onClick: () => void;
}) {
  const score = founderScore(plan);
  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--omega-surface-2, rgba(255,255,255,0.04))',
        border: `1px solid ${selected ? 'var(--omega-green)' : 'var(--omega-border, rgba(255,255,255,0.08))'}`,
        outline: selected ? '2px solid var(--omega-green)' : 'none',
        borderRadius: 6,
        padding: '12px 14px',
        cursor: 'pointer',
        position: 'relative',
        transition: 'background 0.15s',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLDivElement).style.background =
          'var(--omega-surface-2, rgba(255,255,255,0.05))';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLDivElement).style.background =
          'var(--omega-surface-2, rgba(255,255,255,0.04))';
      }}
    >
      {/* Checkbox */}
      <div
        onClick={e => {
          e.stopPropagation();
          onSelect(plan.plan_id, !selected);
        }}
        style={{ position: 'absolute', top: 10, left: 10 }}
      >
        <input
          type="checkbox"
          checked={selected}
          onChange={e => {
            e.stopPropagation();
            onSelect(plan.plan_id, e.target.checked);
          }}
          style={{ cursor: 'pointer', accentColor: 'var(--omega-green)' }}
        />
      </div>

      {/* Title row */}
      <div style={{ paddingLeft: 22, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '10.5pt', color: 'var(--omega-ink)', lineHeight: 1.3 }}>
            {plan.product_name}
          </div>
          <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '8.5pt', marginTop: 2, lineHeight: 1.4 }}>
            {plan.tagline}
          </div>
        </div>
        <ConfidenceRing value={plan.confidence} />
      </div>

      {/* Domain + status */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <Chip>{plan.domain}</Chip>
        {plan.sub_sector && plan.sub_sector !== plan.domain && (
          <Chip>{plan.sub_sector}</Chip>
        )}
        <StatusBadge status={plan.status} />
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Chip color="var(--omega-green)">Y1 {fmt(plan.revenue_projection.year1)}</Chip>
        <Chip>{fmt(plan.market_size_usd)} market</Chip>
        <Chip>{plan.estimated_build_days}d build</Chip>
      </div>

      {/* Founder score */}
      <div>
        <div style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', marginBottom: 3 }}>
          Founder score
        </div>
        <FounderScoreBar score={score} />
      </div>

      {/* Atoms warning */}
      {plan.required_new_atoms.length > 0 && (
        <Chip color="#fbbf24">
          {plan.required_new_atoms.length} new atom{plan.required_new_atoms.length > 1 ? 's' : ''} required
        </Chip>
      )}
    </div>
  );
}

// ─── Collapsible section ───────────────────────────────────────────

function Section({
  title,
  defaultOpen,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <div style={{ borderBottom: '1px solid var(--omega-border, rgba(255,255,255,0.08))', paddingBottom: 12 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--omega-ink)',
          fontFamily: 'var(--omega-mono)',
          fontSize: '9pt',
          fontWeight: 700,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          padding: '8px 0 4px',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          width: '100%',
          textAlign: 'left',
        }}
      >
        <span>{open ? '▾' : '▸'}</span>
        {title}
      </button>
      {open && <div style={{ paddingTop: 6 }}>{children}</div>}
    </div>
  );
}

// ─── Memory section (loads on demand) ─────────────────────────────

function MemorySection({ planId, domain }: { planId: string; domain: string }) {
  const { data, isPending } = usePlanMemoryContext(planId, domain, true);

  if (isPending) {
    return <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '8.5pt' }}>Loading…</div>;
  }
  if (!data || data.results.length === 0) {
    return (
      <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '8.5pt' }}>
        No memory context for this product yet
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {data.results.slice(0, 5).map((m: MemoryEntry) => (
        <div
          key={m.memory_id}
          style={{
            fontSize: '8.5pt',
            color: 'var(--omega-ink)',
            background: 'rgba(255,255,255,0.04)',
            borderRadius: 4,
            padding: '5px 8px',
          }}
        >
          <span style={{ color: 'var(--omega-ink-subtle)', marginRight: 6 }}>[{m.entry_type}]</span>
          {String(
            (m.content as Record<string, unknown>).action ??
            (m.content as Record<string, unknown>).question ??
            JSON.stringify(m.content).slice(0, 60),
          )}
          <span style={{ color: 'var(--omega-ink-subtle)', marginLeft: 8 }}>
            {fmtDate(m.created_at)}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Detail Drawer ─────────────────────────────────────────────────

type ActionState = 'idle' | 'signing' | 'done' | 'error';

function DetailDrawer({
  plan,
  onClose,
  onActionDone,
  onEnrichDone,
}: {
  plan: LaunchPlanSummary;
  onClose: () => void;
  onActionDone: () => void;
  onEnrichDone: (updated: LaunchPlanSummary) => void;
}) {
  const score = founderScore(plan);
  const [rejectInput, setRejectInput] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [actionState, setActionState] = useState<ActionState>('idle');
  const [actionError, setActionError] = useState<string | null>(null);
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const [enrichState, setEnrichState] = useState<'idle' | 'enriching' | 'done' | 'error'>('idle');
  const [enrichError, setEnrichError] = useState<string | null>(null);

  // True when any field the enrichment loop fills is still empty.
  const hasEmptySections =
    !plan.competitive_moat ||
    !plan.cultural_hook ||
    plan.core_features.length === 0 ||
    plan.hidden_substrate_use.length === 0 ||
    plan.first_50_users.length === 0 ||
    !plan.adversary_report_id;

  const runEnrich = useCallback(async () => {
    setEnrichState('enriching');
    setEnrichError(null);
    try {
      const updated = await callEnrichAction(plan.plan_id);
      setEnrichState('done');
      onEnrichDone(updated);
      setTimeout(() => setEnrichState('idle'), 1500);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setEnrichError(msg);
      setEnrichState('error');
    }
  }, [plan.plan_id, onEnrichDone]);

  const runAction = useCallback(
    async (action: string, extraBody?: Record<string, unknown>) => {
      setActionState('signing');
      setActiveAction(action);
      setActionError(null);
      try {
        const body: Record<string, unknown> = { nonce: crypto.randomUUID(), ...extraBody };
        await callAdminAction(plan.plan_id, action, body);
        setActionState('done');
        setTimeout(() => {
          setActionState('idle');
          setActiveAction(null);
          onActionDone();
          onClose();
        }, 800);
      } catch (err) {
        setActionError(err instanceof Error ? err.message : String(err));
        setActionState('error');
        setActiveAction(null);
      }
    },
    [plan.plan_id, onActionDone, onClose],
  );

  const isTerminal = plan.status === 'approved' || plan.status === 'rejected';
  const btnBase: React.CSSProperties = {
    fontFamily: 'var(--omega-mono)',
    fontSize: '9pt',
    fontWeight: 600,
    border: 'none',
    borderRadius: 4,
    padding: '7px 12px',
    cursor: actionState === 'signing' ? 'not-allowed' : 'pointer',
    opacity: actionState === 'signing' ? 0.6 : 1,
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.4)',
          zIndex: 200,
        }}
      />

      {/* Drawer */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(480px, 100vw)',
          background: 'var(--omega-bg, #0a0d12)',
          borderLeft: '1px solid var(--omega-border, rgba(255,255,255,0.12))',
          zIndex: 201,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          fontFamily: 'var(--omega-mono)',
          color: 'var(--omega-ink)',
        }}
      >
        {/* Drawer header */}
        <div
          style={{
            padding: '14px 16px',
            borderBottom: '1px solid var(--omega-border, rgba(255,255,255,0.08))',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: '9pt', color: 'var(--omega-ink-subtle)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
            Plan Detail
          </span>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--omega-ink-subtle)', cursor: 'pointer', fontSize: '14pt' }}
          >
            ×
          </button>
        </div>

        {/* Approved banner */}
        {plan.status === 'approved' && (
          <div
            style={{
              background: 'rgba(110,231,183,0.1)',
              borderBottom: '1px solid rgba(110,231,183,0.3)',
              color: '#6ee7b7',
              fontSize: '8.5pt',
              padding: '8px 16px',
              textAlign: 'center',
              letterSpacing: '0.05em',
            }}
          >
            Already approved — in pipeline
          </div>
        )}

        {/* Sections */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 16px 80px' }}>
          {/* Section 1 — The Pitch */}
          <Section title="The Pitch" defaultOpen>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '9pt' }}>
              <div style={{ fontSize: '13pt', fontWeight: 700, color: 'var(--omega-ink)' }}>
                {plan.product_name}
              </div>
              <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9.5pt' }}>{plan.tagline}</div>
              <div>
                <span style={{ color: 'var(--omega-ink-subtle)' }}>Target user: </span>
                {plan.target_user}
              </div>
              {plan.cultural_hook && (
                <div>
                  <span style={{ color: 'var(--omega-ink-subtle)' }}>Cultural hook: </span>
                  {plan.cultural_hook}
                </div>
              )}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                <Chip>{plan.domain}</Chip>
                {plan.sub_sector && <Chip>{plan.sub_sector}</Chip>}
              </div>
            </div>
          </Section>

          {/* Section 2 — The Numbers */}
          <Section title="The Numbers" defaultOpen>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: '9pt' }}>
              <div style={{ display: 'flex', gap: 20 }}>
                <div>
                  <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '8pt', marginBottom: 2 }}>Market size</div>
                  <div style={{ fontWeight: 600 }}>{fmt(plan.market_size_usd)}</div>
                </div>
                <div>
                  <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '8pt', marginBottom: 2 }}>YoY growth</div>
                  <div style={{ fontWeight: 600 }}>{(plan.growth_rate_yoy * 100).toFixed(0)}%</div>
                </div>
                <div>
                  <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '8pt', marginBottom: 2 }}>Build days</div>
                  <div style={{ fontWeight: 600 }}>{plan.estimated_build_days}</div>
                </div>
                <div>
                  <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '8pt', marginBottom: 2 }}>Confidence</div>
                  <div style={{ fontWeight: 600 }}>{Math.round(plan.confidence * 100)}%</div>
                </div>
              </div>
              {/* Revenue table */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: 1,
                  background: 'var(--omega-border, rgba(255,255,255,0.08))',
                  borderRadius: 4,
                  overflow: 'hidden',
                }}
              >
                {(['year1', 'year2', 'year3'] as const).map((yr, i) => (
                  <div
                    key={yr}
                    style={{
                      background: 'var(--omega-bg, #0d0d0d)',
                      padding: '6px 10px',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '7.5pt' }}>Year {i + 1}</div>
                    <div style={{ fontWeight: 700, fontSize: '10pt', color: 'var(--omega-green)' }}>
                      {fmt(plan.revenue_projection[yr])}
                    </div>
                  </div>
                ))}
              </div>
              <div>
                <div style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', marginBottom: 3 }}>
                  Founder score
                </div>
                <FounderScoreBar score={score} />
              </div>
            </div>
          </Section>

          {/* Section 3 — The Substrate */}
          <Section title="The Substrate">
            {plan.hidden_substrate_use.length === 0 ? (
              <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '8.5pt' }}>
                No GI substrate capabilities specified
              </div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {plan.hidden_substrate_use.map(s => (
                  <Chip key={s} color="var(--omega-green)">{s}</Chip>
                ))}
              </div>
            )}
          </Section>

          {/* Section 4 — The Competition */}
          <Section title="The Competition">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: '9pt' }}>
              <div>
                <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '8pt', marginBottom: 3 }}>Competitive moat</div>
                <div style={{ lineHeight: 1.5 }}>{plan.competitive_moat}</div>
              </div>
              {plan.first_50_users.length > 0 && (
                <div>
                  <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '8pt', marginBottom: 4 }}>
                    First acquisition channels
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {plan.first_50_users.slice(0, 5).map((u, i) => (
                      <div key={i} style={{ fontSize: '8.5pt' }}>
                        <span style={{ color: 'var(--omega-ink)' }}>{u.name}</span>
                        <span style={{ color: 'var(--omega-ink-subtle)' }}> — {u.channel}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Section>

          {/* Section 5 — Validation Report */}
          <Section title="Validation Report">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: '9pt' }}>
              <div>
                <span style={{ color: 'var(--omega-ink-subtle)' }}>Pricing: </span>
                {plan.pricing_model.replace(/_/g, ' ')}
              </div>
              {plan.core_features.length > 0 && (
                <div>
                  <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '8pt', marginBottom: 4 }}>Core features</div>
                  <ul style={{ margin: 0, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {plan.core_features.map((f, i) => (
                      <li key={i} style={{ fontSize: '8.5pt' }}>{f}</li>
                    ))}
                  </ul>
                </div>
              )}
              {plan.required_new_atoms.length > 0 && (
                <div>
                  <div style={{ color: '#fbbf24', fontSize: '8pt', marginBottom: 4 }}>Required new atoms</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {plan.required_new_atoms.map(a => (
                      <Chip key={a} color="#fbbf24">{a}</Chip>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Section>

          {/* Section 6 — Adversary Report */}
          <Section title="Adversary Report">
            {plan.adversary_report_id ? (
              <div style={{ fontSize: '9pt' }}>
                <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '8pt', marginBottom: 4 }}>Report ID</div>
                <div style={{ fontFamily: 'monospace', fontSize: '8pt', color: 'var(--omega-ink)' }}>
                  {plan.adversary_report_id}
                </div>
                <div style={{ color: 'var(--omega-ink-subtle)', marginTop: 6, fontSize: '8.5pt' }}>
                  Threat analysis on record
                </div>
              </div>
            ) : (
              <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '8.5pt' }}>
                No adversary analysis yet
              </div>
            )}
          </Section>

          {/* Section 7 — Memory Context */}
          <Section title="Memory Context">
            <MemorySection planId={plan.plan_id} domain={plan.domain} />
          </Section>
        </div>

        {/* Sticky action bar */}
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            background: 'var(--omega-bg, #0d0d0d)',
            borderTop: '1px solid var(--omega-border, rgba(255,255,255,0.12))',
            padding: '10px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            flexShrink: 0,
          }}
        >
          {actionError && (
            <div style={{ color: 'var(--omega-red, #f87171)', fontSize: '8.5pt' }}>{actionError}</div>
          )}
          {enrichError && (
            <div style={{
              color: '#fff',
              background: 'rgba(248,113,113,0.25)',
              border: '1px solid rgba(248,113,113,0.6)',
              borderRadius: 4,
              padding: '6px 10px',
              fontSize: '9pt',
              fontWeight: 600,
              letterSpacing: '0.02em',
            }}>
              Enrich error: {enrichError}
            </div>
          )}

          {showRejectInput && (
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                autoFocus
                placeholder="Rejection reason…"
                value={rejectInput}
                onChange={e => setRejectInput(e.target.value)}
                style={{
                  flex: 1,
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid var(--omega-border, rgba(255,255,255,0.12))',
                  borderRadius: 4,
                  color: 'var(--omega-ink)',
                  fontFamily: 'var(--omega-mono)',
                  fontSize: '9pt',
                  padding: '5px 8px',
                }}
                onKeyDown={e => {
                  if (e.key === 'Escape') {
                    setShowRejectInput(false);
                    setRejectInput('');
                  }
                }}
              />
              <button
                onClick={() => runAction('reject', { reason: rejectInput })}
                disabled={actionState === 'signing'}
                style={{ ...btnBase, background: 'var(--omega-red, #f87171)', color: '#000' }}
              >
                {actionState === 'signing' && activeAction === 'reject' ? 'Signing…' : 'Confirm'}
              </button>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {!isTerminal && (
              <>
                <button
                  onClick={() => runAction('approve')}
                  disabled={actionState === 'signing'}
                  style={{ ...btnBase, background: 'var(--omega-green)', color: '#000' }}
                >
                  {actionState === 'signing' && activeAction === 'approve'
                    ? 'Signing…'
                    : actionState === 'done' && activeAction === 'approve'
                    ? 'Done'
                    : 'Approve & Build'}
                </button>
                <button
                  onClick={() => runAction('defer')}
                  disabled={actionState === 'signing'}
                  style={{ ...btnBase, background: '#fbbf2422', color: '#fbbf24', border: '1px solid #fbbf2444' }}
                >
                  {actionState === 'signing' && activeAction === 'defer' ? 'Signing…' : 'Defer 24h'}
                </button>
                <button
                  onClick={() => {
                    if (showRejectInput) {
                      setShowRejectInput(false);
                      setRejectInput('');
                    } else {
                      setShowRejectInput(true);
                    }
                  }}
                  disabled={actionState === 'signing'}
                  style={{ ...btnBase, background: 'rgba(248,113,113,0.12)', color: 'var(--omega-red, #f87171)', border: '1px solid rgba(248,113,113,0.3)' }}
                >
                  Reject
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => { void runEnrich(); }}
              disabled={enrichState === 'enriching' || actionState === 'signing'}
              style={{
                ...btnBase,
                background: enrichState === 'done'
                  ? 'rgba(110,231,183,0.15)'
                  : hasEmptySections
                  ? 'rgba(167,139,250,0.18)'
                  : 'rgba(255,255,255,0.07)',
                color: enrichState === 'done'
                  ? 'var(--omega-green)'
                  : hasEmptySections
                  ? '#a78bfa'
                  : 'var(--omega-ink)',
                border: hasEmptySections && enrichState !== 'done'
                  ? '1px solid rgba(167,139,250,0.45)'
                  : '1px solid rgba(255,255,255,0.12)',
                opacity: enrichState === 'enriching' ? 0.6 : 1,
              }}
            >
              {enrichState === 'enriching'
                ? 'Enriching…'
                : enrichState === 'done'
                ? 'Enriched'
                : hasEmptySections
                ? 'Enrich Now'
                : 'Re-Enrich'}
            </button>
            <button
              onClick={() => alert('Feature coming soon')}
              style={{ ...btnBase, background: 'rgba(255,255,255,0.07)', color: 'var(--omega-ink)' }}
            >
              Request Info
            </button>
            <button
              onClick={() => alert('Feature coming soon')}
              style={{ ...btnBase, background: 'rgba(255,255,255,0.07)', color: 'var(--omega-ink)' }}
            >
              Save Note
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Bulk Action Bar ───────────────────────────────────────────────

function BulkBar({
  selectedIds,
  onClear,
  onBulkAction,
}: {
  selectedIds: Set<string>;
  onClear: () => void;
  onBulkAction: (action: string, ids: string[]) => Promise<void>;
}) {
  const [state, setState] = useState<'idle' | 'running' | 'done'>('idle');
  const count = selectedIds.size;

  const run = async (action: string) => {
    setState('running');
    const ids = Array.from(selectedIds);
    for (const id of ids) {
      await callAdminAction(id, action, { nonce: crypto.randomUUID() });
    }
    await onBulkAction(action, ids);
    setState('done');
    setTimeout(() => {
      setState('idle');
      onClear();
    }, 800);
  };

  const btnBase: React.CSSProperties = {
    fontFamily: 'var(--omega-mono)',
    fontSize: '8.5pt',
    fontWeight: 600,
    border: 'none',
    borderRadius: 4,
    padding: '6px 12px',
    cursor: state === 'running' ? 'not-allowed' : 'pointer',
    opacity: state === 'running' ? 0.6 : 1,
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'var(--omega-bg, #0d0d0d)',
        borderTop: '1px solid var(--omega-border, rgba(255,255,255,0.12))',
        padding: '10px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        zIndex: 100,
        fontFamily: 'var(--omega-mono)',
        fontSize: '9pt',
        color: 'var(--omega-ink)',
      }}
    >
      <span style={{ color: 'var(--omega-ink-subtle)', marginRight: 4 }}>
        {count} selected —
      </span>
      <button onClick={() => run('approve')} disabled={state === 'running'} style={{ ...btnBase, background: 'var(--omega-green)', color: '#000' }}>
        {state === 'running' ? 'Working…' : 'Approve All'}
      </button>
      <button onClick={() => run('defer')} disabled={state === 'running'} style={{ ...btnBase, background: '#fbbf2422', color: '#fbbf24', border: '1px solid #fbbf2444' }}>
        Defer All
      </button>
      <button onClick={() => run('reject')} disabled={state === 'running'} style={{ ...btnBase, background: 'rgba(248,113,113,0.12)', color: 'var(--omega-red, #f87171)', border: '1px solid rgba(248,113,113,0.3)' }}>
        Reject All
      </button>
      <button onClick={onClear} style={{ ...btnBase, background: 'rgba(255,255,255,0.07)', color: 'var(--omega-ink)', marginLeft: 'auto' }}>
        Clear
      </button>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────

export function OmegaLaunchPlansPage() {
  const queryClient = useQueryClient();

  // Filter state
  const [activeDomains, setActiveDomains] = useState<Set<string>>(new Set());
  const [status, setStatus] = useState('');
  const [pricingModel, setPricingModel] = useState('');
  const [confidenceMin, setConfidenceMin] = useState(0);
  const [search, setSearch] = useState('');

  // Sort state
  const [sortField, setSortField] = useState<SortField>('confidence');
  const [sortAsc, setSortAsc] = useState(false);

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Drawer
  const [drawerPlan, setDrawerPlan] = useState<LaunchPlanSummary | null>(null);

  // Force discovery
  const [discoveryState, setDiscoveryState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');

  // Enrichment queue visibility toggle
  const [showDrafts, setShowDrafts] = useState(false);

  // Fetch all plans — status filtering is done client-side so the banner
  // can count plans in the enrichment queue regardless of the active filter.
  const firstDomain = activeDomains.size === 1 ? Array.from(activeDomains)[0] : undefined;
  const { data, isPending, isError, refetch } = useLaunchPlans(firstDomain);

  const handleSelect = useCallback((id: string, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const handleDomainChip = useCallback((domain: string) => {
    setActiveDomains(prev => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain);
      else next.add(domain);
      return next;
    });
  }, []);

  const handleActionDone = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: omegaQueryKeys.all });
    void refetch();
  }, [queryClient, refetch]);

  const handleEnrichDone = useCallback((updated: LaunchPlanSummary) => {
    setDrawerPlan(updated);
  }, []);

  const handleBulkAction = useCallback(
    async (_action: string, _ids: string[]) => {
      void queryClient.invalidateQueries({ queryKey: omegaQueryKeys.all });
      void refetch();
    },
    [queryClient, refetch],
  );

  const handleForceDiscovery = async () => {
    setDiscoveryState('running');
    try {
      await forceDiscovery();
      setDiscoveryState('done');
      void refetch();
      setTimeout(() => setDiscoveryState('idle'), 2000);
    } catch {
      setDiscoveryState('error');
      setTimeout(() => setDiscoveryState('idle'), 3000);
    }
  };

  // Client-side filtering
  const allPlans = data?.plans ?? [];
  const enrichingCount = allPlans.filter(p => QUEUE_STATUSES.has(p.status)).length;

  const filtered = allPlans.filter(p => {
    // Status gate: default shows only review-ready plans; showDrafts inverts to queue-only.
    if (showDrafts) {
      if (!QUEUE_STATUSES.has(p.status)) return false;
    } else if (!status) {
      if (!REVIEW_STATUSES.has(p.status)) return false;
    } else {
      if (p.status !== status) return false;
    }
    if (activeDomains.size > 1 && !activeDomains.has(p.domain)) return false;
    if (pricingModel && p.pricing_model !== pricingModel) return false;
    if (p.confidence < confidenceMin / 100) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!p.product_name.toLowerCase().includes(q) && !p.tagline.toLowerCase().includes(q))
        return false;
    }
    return true;
  });

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    let va = 0;
    let vb = 0;
    switch (sortField) {
      case 'confidence':
        va = a.confidence; vb = b.confidence; break;
      case 'year1_revenue':
        va = a.revenue_projection.year1; vb = b.revenue_projection.year1; break;
      case 'market_size':
        va = a.market_size_usd; vb = b.market_size_usd; break;
      case 'build_time':
        va = a.estimated_build_days; vb = b.estimated_build_days; break;
      case 'founder_score':
        va = founderScore(a); vb = founderScore(b); break;
    }
    return sortAsc ? va - vb : vb - va;
  });

  const selectStyle: React.CSSProperties = {
    background: 'var(--omega-surface-2, rgba(255,255,255,0.06))',
    border: '1px solid var(--omega-border, rgba(255,255,255,0.12))',
    borderRadius: 4,
    color: 'var(--omega-ink)',
    fontFamily: 'var(--omega-mono)',
    fontSize: '8.5pt',
    padding: '4px 8px',
  };

  const sortBtnStyle = (field: SortField): React.CSSProperties => ({
    background: sortField === field ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.05)',
    border: '1px solid var(--omega-border, rgba(255,255,255,0.1))',
    borderRadius: 4,
    color: sortField === field ? 'var(--omega-ink)' : 'var(--omega-ink-subtle)',
    fontFamily: 'var(--omega-mono)',
    fontSize: '8pt',
    padding: '3px 8px',
    cursor: 'pointer',
    fontWeight: sortField === field ? 700 : 400,
  });

  return (
    <div
      className="omega-shell"
      style={{
        fontFamily: 'var(--omega-mono)',
        color: 'var(--omega-ink)',
        padding: '16px 20px',
        paddingBottom: selectedIds.size > 0 ? 80 : 16,
        maxWidth: '100%',
        minHeight: 'unset',
      }}
    >
      {/* Header bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 14,
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            fontSize: '11pt',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          Launch Plans
        </span>
        {data && (
          <span
            style={{
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid var(--omega-border, rgba(255,255,255,0.1))',
              borderRadius: 3,
              fontSize: '8pt',
              padding: '1px 7px',
              color: 'var(--omega-ink-subtle)',
            }}
          >
            {data.total}
          </span>
        )}
        <input
          placeholder="Search product / tagline…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            ...selectStyle,
            flex: '1 1 180px',
            minWidth: 140,
            maxWidth: 280,
          }}
        />
        <button
          onClick={handleForceDiscovery}
          disabled={discoveryState === 'running'}
          style={{
            marginLeft: 'auto',
            background: 'rgba(255,255,255,0.07)',
            border: '1px solid var(--omega-border, rgba(255,255,255,0.12))',
            borderRadius: 4,
            color: discoveryState === 'done' ? 'var(--omega-green)' : discoveryState === 'error' ? 'var(--omega-red, #f87171)' : 'var(--omega-ink)',
            fontFamily: 'var(--omega-mono)',
            fontSize: '8.5pt',
            fontWeight: 600,
            padding: '5px 12px',
            cursor: discoveryState === 'running' ? 'not-allowed' : 'pointer',
            opacity: discoveryState === 'running' ? 0.6 : 1,
          }}
        >
          {discoveryState === 'running'
            ? 'Running…'
            : discoveryState === 'done'
            ? 'Triggered'
            : discoveryState === 'error'
            ? 'Failed'
            : 'Force Discovery'}
        </button>
      </div>

      {/* Enrichment queue banner */}
      {enrichingCount > 0 && !showDrafts && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: 'rgba(167,139,250,0.08)',
            border: '1px solid rgba(167,139,250,0.25)',
            borderRadius: 5,
            padding: '7px 14px',
            marginBottom: 10,
            fontSize: '8.5pt',
            color: '#a78bfa',
          }}
        >
          <span style={{ fontWeight: 700, letterSpacing: '0.04em' }}>OMEGA</span>
          <span>
            is enriching {enrichingCount} plan{enrichingCount !== 1 ? 's' : ''} in the background.
            They will appear here when ready.
          </span>
          <button
            onClick={() => setShowDrafts(true)}
            style={{
              marginLeft: 'auto',
              background: 'rgba(167,139,250,0.15)',
              border: '1px solid rgba(167,139,250,0.3)',
              borderRadius: 3,
              color: '#a78bfa',
              fontFamily: 'var(--omega-mono)',
              fontSize: '8pt',
              padding: '2px 8px',
              cursor: 'pointer',
            }}
          >
            View queue
          </button>
        </div>
      )}

      {/* Filter row — domain chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        {DOMAIN_LIST.map(d => (
          <button
            key={d}
            onClick={() => handleDomainChip(d)}
            style={{
              background: activeDomains.has(d) ? 'rgba(110,231,183,0.15)' : 'rgba(255,255,255,0.05)',
              border: `1px solid ${activeDomains.has(d) ? 'var(--omega-green)' : 'var(--omega-border, rgba(255,255,255,0.1))'}`,
              borderRadius: 3,
              color: activeDomains.has(d) ? 'var(--omega-green)' : 'var(--omega-ink-subtle)',
              fontFamily: 'var(--omega-mono)',
              fontSize: '7.5pt',
              padding: '2px 7px',
              cursor: 'pointer',
              fontWeight: activeDomains.has(d) ? 700 : 400,
            }}
          >
            {d}
          </button>
        ))}
      </div>

      {/* Filter row — selects + confidence */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <button
          onClick={() => { setShowDrafts(v => !v); if (showDrafts) setStatus(''); }}
          style={{
            background: showDrafts ? 'rgba(167,139,250,0.15)' : 'rgba(255,255,255,0.05)',
            border: `1px solid ${showDrafts ? 'rgba(167,139,250,0.5)' : 'var(--omega-border, rgba(255,255,255,0.1))'}`,
            borderRadius: 4,
            color: showDrafts ? '#a78bfa' : 'var(--omega-ink-subtle)',
            fontFamily: 'var(--omega-mono)',
            fontSize: '8pt',
            padding: '4px 10px',
            cursor: 'pointer',
            fontWeight: showDrafts ? 700 : 400,
          }}
        >
          {showDrafts ? 'Hide queue' : `Drafts/Enriching${enrichingCount > 0 ? ` (${enrichingCount})` : ''}`}
        </button>
        <select
          value={showDrafts ? '' : status}
          onChange={e => { setStatus(e.target.value); if (showDrafts) setShowDrafts(false); }}
          disabled={showDrafts}
          style={{ ...selectStyle, opacity: showDrafts ? 0.4 : 1 }}
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select value={pricingModel} onChange={e => setPricingModel(e.target.value)} style={selectStyle}>
          <option value="">All pricing</option>
          {PRICING_OPTIONS.map(p => (
            <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)' }}>Confidence ≥</span>
          <input
            type="range"
            min={0}
            max={100}
            value={confidenceMin}
            onChange={e => setConfidenceMin(Number(e.target.value))}
            style={{ width: 90, accentColor: 'var(--omega-green)' }}
          />
          <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink)', minWidth: 28 }}>
            {confidenceMin}%
          </span>
        </div>
      </div>

      {/* Sort row */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <span style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', marginRight: 2 }}>Sort by:</span>
        {(['confidence', 'year1_revenue', 'market_size', 'build_time', 'founder_score'] as SortField[]).map(f => (
          <button
            key={f}
            onClick={() => {
              if (sortField === f) setSortAsc(a => !a);
              else { setSortField(f); setSortAsc(false); }
            }}
            style={sortBtnStyle(f)}
          >
            {f.replace(/_/g, ' ')}
            {sortField === f ? (sortAsc ? ' ↑' : ' ↓') : ''}
          </button>
        ))}
      </div>

      {/* Plan count */}
      <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', marginBottom: 14 }}>
        {sorted.length} plan{sorted.length !== 1 ? 's' : ''}
        {(activeDomains.size > 0 || status || pricingModel || confidenceMin > 0 || search) &&
          ' (filtered)'}
      </div>

      {/* Loading / error */}
      {isPending && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading plans…</div>
      )}
      {isError && (
        <div style={{ color: 'var(--omega-red, #f87171)', fontSize: '9pt' }}>
          Failed to load plans.
        </div>
      )}

      {/* Empty state */}
      {!isPending && data && sorted.length === 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 16,
            padding: '60px 0',
            color: 'var(--omega-ink-subtle)',
          }}
        >
          <div style={{ fontSize: '10pt' }}>No plans found.</div>
          <button
            onClick={handleForceDiscovery}
            disabled={discoveryState === 'running'}
            style={{
              background: 'rgba(255,255,255,0.07)',
              border: '1px solid var(--omega-border, rgba(255,255,255,0.12))',
              borderRadius: 4,
              color: 'var(--omega-ink)',
              fontFamily: 'var(--omega-mono)',
              fontSize: '9pt',
              fontWeight: 600,
              padding: '8px 18px',
              cursor: 'pointer',
            }}
          >
            Force Discovery Cycle Now
          </button>
        </div>
      )}

      {/* Card grid */}
      {sorted.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 16,
          }}
        >
          {sorted.map(plan => (
            <PlanCard
              key={plan.plan_id}
              plan={plan}
              selected={selectedIds.has(plan.plan_id)}
              onSelect={handleSelect}
              onClick={() => setDrawerPlan(plan)}
            />
          ))}
        </div>
      )}

      {/* Detail drawer */}
      {drawerPlan && (
        <DetailDrawer
          plan={drawerPlan}
          onClose={() => setDrawerPlan(null)}
          onActionDone={handleActionDone}
          onEnrichDone={handleEnrichDone}
        />
      )}

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <BulkBar
          selectedIds={selectedIds}
          onClear={() => setSelectedIds(new Set())}
          onBulkAction={handleBulkAction}
        />
      )}
    </div>
  );
}
